import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateRoom, useReorderRooms, useRoomList } from "@/hooks/useRooms";
import { RoomTypeCard } from "@/components/rooms/RoomTypeCard";

interface BusinessPhoto {
  id: string;
  url: string;
  isCover: boolean;
}

interface Props {
  businessId: string;
  businessPhotos: BusinessPhoto[];
}

export const RoomsTab = ({ businessId, businessPhotos }: Props) => {
  const { data: rooms, isLoading } = useRoomList(businessId);
  const createMut = useCreateRoom(businessId);
  const reorderMut = useReorderRooms(businessId);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const sorted = [...(rooms ?? [])].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (dateA !== dateB) return dateA - dateB;
    return a.id.localeCompare(b.id);
  });

  const onAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await createMut.mutateAsync({
        businessId,
        name,
        bedCount: 1,
        pricePerNight: 0,
        durationPrices: [],
      });
      toast.success("Room type added");
      setNewName("");
      setAdding(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to add room");
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sorted.findIndex((r) => r.id === active.id);
    const newIdx = sorted.findIndex((r) => r.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    try {
      await reorderMut.mutateAsync(reordered.map((r) => r.id));
    } catch (e: any) {
      toast.error(e?.message ?? "Reorder failed");
    }
  };

  if (isLoading)
    return <p className="text-sm text-muted-foreground p-4">Loading…</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Define room types for this centre. Each type can have its own floor, AC
        status, duration pricing, and photos.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={sorted.map((r) => r.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {sorted.map((room) => (
              <RoomTypeCard
                key={room.id}
                room={room}
                businessId={businessId}
                businessPhotos={businessPhotos}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {adding ? (
        <div className="flex gap-2 items-center">
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Room type name (e.g. Deluxe AC)"
            onKeyDown={(e) => {
              if (e.key === "Enter") onAdd();
              if (e.key === "Escape") setAdding(false);
            }}
          />
          <Button
            type="button"
            size="sm"
            onClick={onAdd}
            disabled={!newName.trim() || createMut.isPending}
          >
            {createMut.isPending ? "Adding…" : "Add"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setAdding(false);
              setNewName("");
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => setAdding(true)}
          className="w-full border-dashed"
        >
          <Plus className="w-4 h-4 mr-2" /> Add room type
        </Button>
      )}
    </div>
  );
};
